import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Instagram, Twitter, Youtube, Linkedin, Mail, MapPin } from 'lucide-react';
import { Logo, CardCorner, TranslatedText } from '../ui';

const socialLinks = [
  { icon: Instagram, href: 'https://www.instagram.com/btalentstar/', label: 'Instagram' },
  { icon: Twitter, href: 'https://twitter.com/btalentstar', label: 'Twitter' },
  { icon: Youtube, href: 'https://www.youtube.com/@btalentstar', label: 'YouTube' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/btalentstar', label: 'LinkedIn' },
];

const legalLinks = [
  {
    key: 'privacy',
    href: '/politicas?tab=privacy',
  },
  {
    key: 'terms',
    href: '/politicas?tab=terms',
  },
  {
    key: 'cookies',
    href: '#',
  },
  {
    key: 'deleteAccount',
    href: '/eliminar-data',
  },
];

const footerLinks = {
  product: ['features', 'pricing', 'faq', 'download'],
  company: ['about', 'careers', 'press', 'contact'],
};

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="relative bg-brand-darkNavy overflow-hidden">
      {/* Top border glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/50 to-transparent" />
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,215,0,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,215,0,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>
      
      {/* Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-yellow/5 rounded-full blur-[150px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer */}
        <div className="py-16 md:py-20">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Brand column */}
            <div className="lg:col-span-1 space-y-6">
              <Logo />
              <p className="text-dark-400 leading-relaxed">
                <TranslatedText 
                  i18nKey="footer.description" 
                  type="fade"
                  duration={0.5}
                />
              </p>
              
              {/* Social links */}
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-xl bg-brand-blue flex items-center justify-center text-dark-400 hover:text-brand-yellow hover:bg-brand-lightBlue transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Links columns */}
            <div className="lg:col-span-2 grid grid-cols-3 gap-8">
              {Object.entries(footerLinks).map(([category, links]) => (
                <div key={category}>
                  <h3 className="font-bold text-white uppercase tracking-wider text-sm mb-4">
                    <TranslatedText 
                      i18nKey={`footer.${category}`}
                      type="fade"
                      duration={0.3}
                    />
                  </h3>
                  <ul className="space-y-3">
                    {links.map((link) => (
                      <li key={link}>
                        <motion.a
                          href="#"
                          whileHover={{ x: 4 }}
                          className="text-dark-400 hover:text-brand-yellow transition-colors text-sm"
                        >
                          <TranslatedText 
                            i18nKey={`footer.${link}`}
                            type="fade"
                            duration={0.3}
                          />
                        </motion.a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Legal links with real hrefs */}
              <div>
                <h3 className="font-bold text-white uppercase tracking-wider text-sm mb-4">
                  <TranslatedText i18nKey="footer.legal" type="fade" duration={0.3} />
                </h3>
                <ul className="space-y-3">
                  {legalLinks.map((link) => {
                    const isExternal = link.href.startsWith('http');
                    return (
                    <li key={link.key}>
                      <motion.a
                        href={link.href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        whileHover={{ x: 4 }}
                        className="text-dark-400 hover:text-brand-yellow transition-colors text-sm"
                      >
                        <TranslatedText 
                          i18nKey={`footer.${link.key}`}
                          type="fade"
                          duration={0.3}
                        />
                      </motion.a>
                    </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Contact card */}
            <div className="lg:col-span-1">
              <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-lightBlue/30 rounded-2xl p-6">
                <CardCorner position="top-left" />
                <CardCorner position="bottom-right" />
                
                <h3 className="font-bold text-white mb-4">
                  <TranslatedText 
                    i18nKey="footer.contactTitle"
                    type="fade"
                    duration={0.4}
                  />
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm text-dark-400">
                    <Mail className="w-5 h-5 text-brand-yellow shrink-0 mt-0.5" />
                    <a href="mailto:contact@btalentstar.com" className="hover:text-brand-yellow transition-colors">
                      contact@btalentstar.com
                    </a>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-dark-400">
                    <MapPin className="w-5 h-5 text-brand-yellow shrink-0 mt-0.5" />
                    <span>Santiago, Chile</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 border-t border-brand-lightBlue/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-dark-500">
            <p>© {new Date().getFullYear()} BTalentStar. <TranslatedText i18nKey="footer.rights" type="fade" duration={0.3} /></p>
            <div className="flex items-center gap-2">
              <span>{t('footer.madeWith')}</span>
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-accent-rose"
              >
                ❤️
              </motion.span>
              <span>{t('footer.forTalents')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
